import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Heading,
    Text,
    Stack,
    Divider,
    Skeleton,
    IconButton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ReturnOrderPolicyAlert, ReturnAlert} from './return-order'
import {QRCodeIcon, LeftArrow} from '../../../components/icons'
import Barcode from 'react-barcode'
import {FormattedMessage} from 'react-intl'

export const OrderHeading = ({headingRef, isLoading, order, showBackArrow = true, onBackClick}) => {
    const [showBarcode, setShowBarcode] = useState(false)

    return (
        <Stack spacing={[1, 2]} mb={{base: 0, lg: 8}}>
            <Heading
                as="h1"
                fontSize={['lg', '2xl']}
                tabIndex="0"
                ref={headingRef}
                textTransform="uppercase"
                mb={{base: 8, lg: 0}}
                lineHeight={{base: 'normal', lg: '30px'}}
            >
                <Flex alignItems="center" gap="64px">
                    {showBackArrow && (
                        onBackClick ? (
                            <Box
                                as="button"
                                display={{base: 'inline-block', lg: 'none'}}
                                onClick={onBackClick}
                                cursor="pointer"
                                bg="transparent"
                                border="none"
                                p={0}
                            >
                                <LeftArrow />
                            </Box>
                        ) : (
                            <Link display={{base: 'inline-block', lg: 'none'}} href="/account/orders">
                                <LeftArrow />
                            </Link>
                        )
                    )}
                    <FormattedMessage
                        defaultMessage="Order Details"
                        id="account_order_detail.title.order_details"
                    />
                </Flex>
            </Heading>

            {!isLoading ? (
                <Stack
                    direction={['column', 'row']}
                    alignItems={['flex-start', 'center']}
                    spacing={[0, 3]}
                    divider={
                        <Divider
                            visibility={{base: 'hidden', lg: 'visible'}}
                            orientation={{lg: 'vertical'}}
                            h={[0, 4]}
                        />
                    }
                >
                    <Stack direction="column" alignItems="start" maxW="100%">
                        <Box>
                            <Box display={{base: 'block', lg: 'none'}}>
                                <ReturnAlert orderItems={order.items} />

                                <ReturnOrderPolicyAlert
                                    {...order.returnableStatus}
                                    containerProps={{mb: 6}}
                                />
                            </Box>
                            <Text
                                fontSize="md"
                                fontWeight={700}
                                textTransform="uppercase"
                                lineHeight="20px"
                            >
                                <FormattedMessage
                                    defaultMessage="Order #{orderNo}"
                                    id="account_order_detail.label.order_number"
                                    values={{
                                        orderNo: (
                                            <Text as="span" data-private>
                                                {order.orderNo}
                                            </Text>
                                        )
                                    }}
                                />
                                <IconButton
                                    variant="unstyled"
                                    display={{base: 'inline-flex', lg: 'none'}}
                                    w="20px"
                                    h="20px"
                                    ml="2"
                                    minW={'auto'}
                                    onClick={() => setShowBarcode((s) => !s)}
                                    icon={<QRCodeIcon
                                        w="20px"
                                        h="20px"
                                    />}
                                    aria-label="Show QR Code"
                                />
                            </Text>
                        </Box>
                        {/* Mobile only - barcode when toggled */}
                        {showBarcode && (
                            <Flex
                                display={{base: 'flex', lg: 'none'}}
                                width="100%"
                                alignItems="center"
                                sx={{
                                    '.order-qr-code': {
                                        width: '100%',
                                        height: 'auto',
                                        marginTop: '12px'
                                    }
                                }}
                            >
                                <Barcode
                                    className="order-qr-code"
                                    width={5}
                                    height={120}
                                    displayValue={false}
                                    value={order.orderNo}
                                    margin={0}
                                />
                            </Flex>
                        )}
                    </Stack>
                </Stack>
            ) : (
                <Skeleton h="20px" w="192px" />
            )}
        </Stack>
    )
}

OrderHeading.propTypes = {
    headingRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),
    isLoading: PropTypes.bool,
    order: PropTypes.shape({
        orderNo: PropTypes.string,
        items: PropTypes.array,
        returnableStatus: PropTypes.object
    }),
    showBackArrow: PropTypes.bool,
    onBackClick: PropTypes.func
}
